import React, { useEffect, useState } from 'react';
import PageContainer from '../../../components/PageContainer';
import { Box, Button, capitalize, Chip, CircularProgress, Dialog, DialogActions, DialogContent, Grid, IconButton } from '@mui/material';
import * as Yup from 'yup';
import { Add, DeleteOutline, DeleteOutlined, Edit } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import appRoutes from '../../../routes/routeConfig';
import moment from 'moment';
import Filters from '../../../components/Filters';
import { DataGrid } from '@mui/x-data-grid';
import { getStatusColor } from '../../../util';
import { useSnackbar } from 'notistack';
import { addFaq, addRole, deleteFaq, deleteSubcategory, getAllCategories, getAllSubcategories, getFaqs, getRoles, removeRole, saveSubcategory } from '../../../redux/api/userMgt';
import { Form, Formik } from 'formik';
import TextInput from '../../../components/common/TextInput';
import SelectInput from '../../../components/common/SelectInput';

const SubCategories = () => {
    const defaultSubCategory = {
        isActive: true,
        status: '',
        categoryId: 0,
        code: '',
        name: '',
        slaDays: 0,
        slaDays2: 0,
    };
    const [processing, setProcessing] = useState(false);
    const [actionModal, setActionModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedSubCategory, setSelectedSubCategory] = useState(defaultSubCategory);
    const { enqueueSnackbar } = useSnackbar();
    const [records, setRecords] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [categories, setCategories] = useState([]);

    const handleSearch = (values) => {
        setSearchResults(
            records.filter((record) => {
                let valid = true;
                if (values.name) {
                    valid = valid && record.name.toLowerCase().includes(values.name.toLowerCase());
                }

                if (values.code) {
                    valid = valid && record.code.toLowerCase().includes(values.code.toLowerCase());
                }

                if (values.categoryName) {
                    valid = valid && record.categoryName.toLowerCase().includes(values.categoryName.toLowerCase());
                }

                return valid;
            })
        );
    };

    const columns = [
        {
            field: 'sn',
            headerName: 'S/N',
            width: 80,
            sortable: false,
            renderCell: (params) => {
                return <p style={{ margin: 'auto' }}>{(params?.api?.getRowIndex(params?.row?.code) || 0) + 1}</p>;
            },
        },
        {
            field: 'action',
            headerName: 'Action',
            width: 100,
            sortable: false,
            renderCell: (params) => {
                return (
                    <div
                        style={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            width: '100%',
                        }}
                    >
                        <IconButton
                            size="small"
                            color={'primary'}
                            onClick={() => {
                                setSelectedSubCategory(params?.row || null);
                                setActionModal(true);
                            }}
                            variant="outlined"
                            title={'Edit Sub Category'}
                        >
                            <Edit />
                        </IconButton>

                        <IconButton
                            size="small"
                            color={'error'}
                            onClick={() => {
                                setSelectedSubCategory(params?.row || null);
                                setDeleteModal(true);
                            }}
                            variant="outlined"
                            title={'Delete Sub Category'}
                        >
                            <DeleteOutline />
                        </IconButton>
                    </div>
                );
            },
        },
        { field: 'code', headerName: 'Code', width: 130 },
        { field: 'name', headerName: 'Name', width: 350 },
        { field: 'categoryName', headerName: 'Category', width: 400 },
        { field: 'slaDays', headerName: 'SLA Days', width: 200 },
        { field: 'isActive', headerName: 'Active', width: 200 },
        { field: 'createdBy', headerName: 'Created By', width: 250 },
    ];

    const init = async () => {
        try {
            setProcessing(true);
            const res = await getAllSubcategories();
            const resp = await getAllCategories();
            setRecords(res?.resultObject ?? []);
            setSearchResults(res?.resultObject ?? []);
            setCategories(resp?.resultObject ?? []);
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        } finally {
            setProcessing(false);
        }
    };

    const remove = async (values) => {
        try {
            await deleteSubcategory(values.code || '');
            setDeleteModal(false);
            setSelectedSubCategory(defaultSubCategory);
            enqueueSnackbar('Sub Category Deleted Successfully');
            init();
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    const create = async (values) => {
        try {
            const resp = await saveSubcategory({ ...values });
            if (resp?.isSuccess) {
                setSelectedSubCategory(defaultSubCategory);
                enqueueSnackbar(`Sub Category ${values?.categoryId ? 'Edited' : 'Created'} Successfully`);
                setActionModal(false);
                init();
            } else {
                enqueueSnackbar(resp?.message || `An error occurred`, {
                    variant: 'error',
                });
            }
        } catch (err) {
            enqueueSnackbar(err?.message || 'An error occurred', {
                variant: 'error',
            });
        }
    };

    useEffect(() => {
        init();
    }, []);

    return (
        <PageContainer
            pageTitle="Sub Categories"
            tagline="Manage all sub categories"
            processing={processing}
            buttonComponent={
                <>
                    <Button onClick={() => setActionModal(true)} startIcon={<Add />} variant="contained">
                        Create Sub Category
                    </Button>
                </>
            }
        >
            <Filters
                searchFields={[
                    { id: 'code', label: 'Code' },
                    { id: 'name', label: 'Name' },
                    { id: 'categoryName', label: 'Category' },
                ]}
                searchFunction={handleSearch}
                useDefault={false}
                showClearBtn={false}
            />

            <Box>
                <div className="paper" style={{ height: 550, width: '100%', textAlign: 'center' }}>
                    <DataGrid
                        className={'center-first-column'}
                        rows={searchResults}
                        getRowId={(row) => row.code}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10]}
                        checkboxSelection={false}
                        disableSelectionOnClick
                        style={{ fontSize: '12px' }}
                    />
                </div>
            </Box>

            <Dialog open={actionModal} maxWidth="sm" PaperProps={{ style: { minWidth: '50vw' } }}>
                <Formik initialValues={{ ...selectedSubCategory }} enableReinitialize onSubmit={create}>
                    {({ isSubmitting, values }) => (
                        <Form>
                            <DialogContent>
                                <h4 className="mb-1">{selectedSubCategory?.status ? 'Edit' : 'Create'} Sub Category</h4>
                                <Grid container spacing={3}>
                                    <Grid item xs={12}>
                                        <TextInput readOnly={selectedSubCategory?.status} id="code" required label="Code" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextInput id="name" required label="Name" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextInput id="slaDays" required label="SLA Days" type="number" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextInput id="slaDays2" required label="SLA Days 2" type="number" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <SelectInput id="categoryId" options={categories} required field="categoryId" label="Category" />
                                    </Grid>
                                </Grid>
                            </DialogContent>
                            <DialogActions className="pb-1 pr-1">
                                <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={() => {
                                        setActionModal(false);
                                        setSelectedSubCategory(defaultSubCategory);
                                    }}
                                    disabled={isSubmitting}
                                >
                                    Cancel
                                </Button>
                                <Button variant="contained" color={'primary'} type="submit" disabled={isSubmitting}>
                                    {Boolean(isSubmitting) ? <CircularProgress color="inherit" size={15} /> : selectedSubCategory?.status ? 'Edit' : 'Create'}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>

            <Dialog open={Boolean(deleteModal)}>
                <h4 className="text-center mt-2 mb-0">Confirm Sub Category Deletion</h4>
                <Formik initialValues={{ ...selectedSubCategory }} enableReinitialize onSubmit={remove}>
                    {({ isSubmitting, values }) => (
                        <Form>
                            <DialogContent>
                                <Grid container spacing={3} paddingBottom={3}>
                                    <Grid item xs={12}>
                                        Kindly confirm you want to delete sub category with details below:
                                        <br />
                                        <br />
                                        <b>Name:</b> {values?.name} <br />
                                        <b>Category:</b> {values?.categoryName} <br />
                                    </Grid>

                                    <Grid item xs={6}>
                                        <Button
                                            disabled={isSubmitting}
                                            fullWidth
                                            variant="contained"
                                            color="secondary"
                                            onClick={() => {
                                                setDeleteModal(false);
                                                setSelectedSubCategory(defaultSubCategory);
                                            }}
                                            style={{ marginTop: '.5rem' }}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Button disabled={isSubmitting} startIcon={<DeleteOutlined />} color="error" fullWidth variant="contained" type="submit" style={{ marginTop: '.5rem' }}>
                                            {isSubmitting ? <CircularProgress size={15} color="inherit" /> : 'Delete Sub Category'}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </DialogContent>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </PageContainer>
    );
};

export default SubCategories;
